import { useState } from 'react';
import { useLocation } from 'react-router';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Menu } from 'lucide-react';

import { useTranslations } from '@/i18n/use-locale';
import { interactive } from '@/components/ui/interactive';
import { cn } from '@/lib/utils';
import { SidebarBody, type AdminSidebarProps } from '@/components/admin/admin-sidebar';

/**
 * The nav on a narrow viewport (below `lg`, where the fixed sidebar is hidden):
 * a hamburger that opens the sidebar as a Radix Dialog drawer.
 *
 * On Radix Dialog rather than a hand-rolled panel for the same reason the record
 * modals are — the focus trap, `aria-modal`, Escape-to-close and focus-return
 * come with it, so the console stays keyboard- and screen-reader-navigable at
 * 768px, not just usable to a mouse.
 */
export function AdminMobileNav({ badges, user, permissions }: AdminSidebarProps) {
  const t = useTranslations('admin.nav');
  const pathname = useLocation().pathname;
  // The route the drawer was opened on, or nothing. A nav link changes the route but
  // would otherwise leave the drawer open over the screen it just opened — so "open"
  // means "opened on the route we are still on", and navigating closes it with no
  // effect to run and no frame where it is still showing.
  const [openedOn, setOpenedOn] = useState<string | null>(null);
  const open = openedOn === pathname;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => setOpenedOn(next ? pathname : null)}>
      <DialogPrimitive.Trigger
        aria-label={t('openMenu')}
        className={cn(
          interactive,
          'grid h-10 w-10 shrink-0 place-items-center rounded-lg text-conc-600 hover:bg-pitch-50 hover:text-pitch-800 lg:hidden',
        )}
      >
        <Menu aria-hidden className="h-5 w-5" strokeWidth={1.9} />
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-navy/40 lg:hidden" />
        <DialogPrimitive.Content className="fixed inset-y-0 start-0 z-50 flex w-64 flex-col border-e border-conc-100 bg-white outline-none lg:hidden">
          <DialogPrimitive.Title className="sr-only">{t('label')}</DialogPrimitive.Title>
          <SidebarBody badges={badges} user={user} permissions={permissions} />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
